import { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useTranslate from 'Hooks/useTranslate';

export type Owner = {
  readonly firstName?: string | null;
  readonly lastName?: string | null;
};

type Props = {
  readonly owners?: readonly Owner[] | null;
  /** Value displayed when there is no owner. */
  readonly emptyValue?: string;
};

/** Above this number of owners, only the first {@link TRUNCATED_COUNT} are shown, followed by an "i" icon revealing all. */
const DISPLAY_LIMIT = 5;
const TRUNCATED_COUNT = 4;

const formatOwner = (owner: Owner): string =>
  `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim();

/**
 * Displays organization owners alphabetically, comma-separated.
 * When there are more than {@link DISPLAY_LIMIT} owners, only the first {@link TRUNCATED_COUNT}
 * are shown followed by an "i" icon; clicking it reveals every owner name.
 */
export function OwnersList({ owners, emptyValue = '-' }: Props) {
  const ts = useTranslate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const names = (owners ?? [])
    .map(formatOwner)
    .filter(name => name.length > 0)
    .sort((a, b) => a.localeCompare(b));

  if (names.length === 0) {
    return <Typography component="span">{emptyValue}</Typography>;
  }

  if (names.length <= DISPLAY_LIMIT) {
    return <Typography component="span">{names.join(', ')}</Typography>;
  }

  const open = Boolean(anchorEl);

  return (
    <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
      <Typography component="span">
        {names.slice(0, TRUNCATED_COUNT).join(', ')}
      </Typography>
      <IconButton
        size="small"
        aria-label={ts('component.ownersList.showAll')}
        onClick={event => setAnchorEl(event.currentTarget)}
        data-testid="owners-show-all"
      >
        <InfoOutlinedIcon fontSize="small" />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2} maxWidth={320}>
          <Typography variant="subtitle2" gutterBottom>
            {ts('component.ownersList.title')}
          </Typography>
          {names.map(name => (
            <Typography key={name} variant="body2">
              {name}
            </Typography>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}

export default OwnersList;
